import { useEffect, useRef, useState } from "react";
import { CountrySelect } from "./CountrySelect";
import { Link } from "react-router-dom";
import { useLocalePath, useT } from "../../i18n";
import { api } from "../../lib/api";
import { track } from "../../lib/analytics";
import { Reveal } from "../Reveal";

export const INPUT = "w-full rounded-none border border-ink/20 bg-white px-4 text-base text-ink transition-colors duration-300 placeholder:text-ink/35 hover:border-ink/45 focus:border-ink focus:outline-none aria-[invalid=true]:border-[#9f1d24]";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const PageHero = ({ eyebrow, title, intro, testId }) => (
  <section className="bg-ink pb-16 pt-36 text-white lg:pb-24 lg:pt-48" data-testid={testId}>
    <Reveal className="container-x">
      <p className="eyebrow flex items-center gap-4 text-white/55"><span className="h-px w-8 bg-current" aria-hidden />{eyebrow}</p>
      <h1 className="h-section font-display mt-6 max-w-3xl" data-testid="page-title">{title}</h1>
      {intro && <p className="mt-6 max-w-xl text-white/65">{intro}</p>}
    </Reveal>
  </section>
);

// spec: { field: { req, max, email, int } }
export const check = (form, spec, t) => {
  const e = {};
  Object.entries(spec).forEach(([k, r]) => {
    const v = typeof form[k] === "string" ? form[k].trim() : form[k];
    if (r.req && !v) e[k] = t("form.errors.required");
    else if (v && r.email && !EMAIL_RE.test(v)) e[k] = t("form.errors.email");
    else if (v && r.int && !/^\d+$/.test(String(v).replace(/[,\s]/g, ""))) e[k] = t("form.errors.number");
    else if (v && r.max && String(v).length > r.max) e[k] = t("form.errors.tooLong");
  });
  if (!form.consent) e.consent = t("form.errors.consent");
  return e;
};

export const toInt = (v) => (String(v ?? "").trim() ? parseInt(String(v).replace(/[,\s]/g, ""), 10) : null);

export const useRequestForm = (initial) => {
  const t = useT();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("idle");
  const [serverError, setServerError] = useState("");
  const started = useRef(Date.now());

  const set = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };
  const send = async (url, errs, makeBody) => {
    setErrors(errs);
    setServerError("");
    if (Object.keys(errs).length) return;
    setPhase("sending");
    try {
      await api.post(url, makeBody(Date.now() - started.current));
      track("generate_lead", { form_type: url.split("/").pop() === "inquiries" ? "vehicle" : url.split("/").pop() });
      setPhase("success");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 422 && Array.isArray(err.response.data.fields)) {
        setErrors(Object.fromEntries(err.response.data.fields.map((f) => [f, t("form.errors.invalid")])));
      }
      const key = status === 429 ? "rate" : status === 422 ? "invalid" : status === 502 ? "storage" : detail === "too_fast" ? "tooFast" : detail === "consent_required" ? "consent" : "generic";
      setServerError(t(`form.errors.${key}`));
      setPhase("idle");
    }
  };
  const reset = () => {
    setForm(initial);
    setErrors({});
    setPhase("idle");
    started.current = Date.now();
  };
  return { form, setForm, set, errors, phase, serverError, send, reset };
};

export const Field = ({ id, label, optional, error, children, className = "" }) => {
  const t = useT();
  return (
    <div className={className}>
      <label htmlFor={id} className="micro-label mb-2 flex items-center gap-2 text-ink/70">
        {label}
        {optional && <span className="normal-case tracking-normal text-ink/40">({t("form.optional")})</span>}
      </label>
      {children}
      {error && <p id={`${id}-error`} className="mt-1.5 text-[13px] text-[#9f1d24]" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
};

const aria = (id, error) => ({ "aria-invalid": error ? "true" : undefined, "aria-describedby": error ? `${id}-error` : undefined });

export const Text = ({ ctx, prefix, k, label, optional, className, ...p }) => {
  const id = `${prefix}-${k}`;
  return (
    <Field id={id} label={label} optional={optional} error={ctx.errors[k]} className={className}>
      <input id={id} value={ctx.form[k]} onChange={ctx.set(k)} className={`${INPUT} h-12`} data-testid={id} {...aria(id, ctx.errors[k])} {...p} />
    </Field>
  );
};

export const Area = ({ ctx, prefix, k, label, optional, className, ...p }) => {
  const id = `${prefix}-${k}`;
  return (
    <Field id={id} label={label} optional={optional} error={ctx.errors[k]} className={className}>
      <textarea id={id} rows={5} value={ctx.form[k]} onChange={ctx.set(k)} className={`${INPUT} resize-y py-3`} data-testid={id} {...aria(id, ctx.errors[k])} {...p} />
    </Field>
  );
};

export const Choice = ({ ctx, prefix, k, label, options, optional, className }) => {
  const id = `${prefix}-${k}`;
  return (
    <Field id={id} label={label} optional={optional} error={ctx.errors[k]} className={className}>
      <select id={id} value={ctx.form[k]} onChange={ctx.set(k)} className={`${INPUT} h-12 appearance-none`} data-testid={id} {...aria(id, ctx.errors[k])}>
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
    </Field>
  );
};

export const CountryField = ({ ctx, prefix, k, label, optional, exclude, className }) => {
  const id = `${prefix}-${k}`;
  return (
    <Field id={id} label={label} optional={optional} error={ctx.errors[k]} className={className}>
      <CountrySelect id={id} value={ctx.form[k]} onChange={(code) => ctx.set(k)({ target: { value: code, type: "text" } })} exclude={exclude} testId={id} invalid={!!ctx.errors[k]} />
    </Field>
  );
};

export const FormGroup = ({ title, children }) => (
  <fieldset className="grid gap-5 border-t border-ink/15 pt-6">
    <legend className="micro-label float-left mb-1 w-full text-ink/50">{title}</legend>
    {children}
  </fieldset>
);

export const FormFooter = ({ ctx, prefix, submitLabel }) => {
  const t = useT();
  const lp = useLocalePath();
  return (
    <>
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${prefix}-website`}>Website</label>
        <input id={`${prefix}-website`} tabIndex={-1} autoComplete="off" value={ctx.form.website} onChange={ctx.set("website")} data-testid={`${prefix}-honeypot`} />
      </div>
      <div>
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink/75">
          <input type="checkbox" checked={ctx.form.consent} onChange={ctx.set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[#0a0a0a]" data-testid={`${prefix}-consent`} />
          <span>
            {t("form.consentPre")}
            <Link to={lp("privacy")} target="_blank" className="underline underline-offset-4 hover:text-ink">{t("form.consentLink")}</Link>
            {t("form.consentPost")}
          </span>
        </label>
        {ctx.errors.consent && <p className="mt-1.5 text-[13px] text-[#9f1d24]" data-testid={`${prefix}-consent-error`}>{ctx.errors.consent}</p>}
      </div>
      {ctx.serverError && <p className="border-l-2 border-[#9f1d24] pl-3 text-sm text-[#9f1d24]" role="alert" data-testid={`${prefix}-server-error`}>{ctx.serverError}</p>}
      <button type="submit" disabled={ctx.phase === "sending"} className="btn btn-dark w-full sm:w-auto sm:min-w-[260px]" data-testid={`${prefix}-submit`}>
        {ctx.phase === "sending" ? `${t("form.sending")}…` : submitLabel}
      </button>
    </>
  );
};

export const Success = ({ prefix, onAgain }) => {
  const t = useT();
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.getBoundingClientRect().top < 80) el.scrollIntoView({ block: "center" });
  }, []);
  return (
    <div ref={ref} className="border-t border-ink/15 py-16" data-testid={`${prefix}-success`} role="status">
      <p className="font-display text-4xl">{t("form.successTitle")}</p>
      <p className="mt-4 max-w-md text-ink/60">{t("form.successBody")}</p>
      <button type="button" onClick={onAgain} className="btn btn-ghost-dark mt-10" data-testid={`${prefix}-again`}>{t("form.again")}</button>
    </div>
  );
};

export const NextSteps = ({ label }) => {
  const t = useT();
  return (
    <div data-testid="form-next-steps">
      <p className="micro-label text-ink/45">{label}</p>
      <p className="font-display mt-8 text-2xl">{t("form.next")}</p>
      <ol className="mt-5 border-t border-ink/15">
        {t("form.nextSteps").map((s, i) => (
          <li key={i} className="flex gap-4 border-b border-ink/15 py-4 text-sm text-ink/70"><span className="micro-label tabular-nums text-ink/35">0{i + 1}</span>{s}</li>
        ))}
      </ol>
    </div>
  );
};

export const FormLayout = ({ aside, children }) => (
  <section className="bg-white py-16 text-ink lg:py-24">
    <div className="container-x grid gap-12 lg:grid-cols-12 lg:gap-10">
      <aside className="lg:sticky lg:top-32 lg:col-span-4 lg:self-start">{aside}</aside>
      <div className="relative lg:col-span-7 lg:col-start-6">{children}</div>
    </div>
  </section>
);
